import { useMemo } from 'react'
import {
  ProductFeatureV2,
  RoundRobinExpectedHostV2,
} from '@chilipiper/api-client/src/user-availability'
import { PublicOwnershipSchedulingLink } from '@chilipiper/api-client/src/team-scheduling'
import { getLinkProductFeature } from '../helpers/product-feature'
import {
  BookerSettings,
  getRoundRobinAndOwnershipExpectedHost,
} from '../../load-availability-options'
import {
  getCrmExplicitsFromMeetingAttemptProductFeature,
  getDistributionIdsFromOwnershipAssignments,
  getDistributionIdsFromAssigneeEvaluation,
  transformOwnershipAssignmentsToRoutingResult,
  transformAssigneeEvaluationToRoutingResult,
  applySelectedAssigneesToRoutingResult,
  getFirstAssignmentFromRoutingResult,
} from '../helpers'
import { OwnershipLinkRoutingResult } from '../types'
import { createCrmExplicits } from '../../smart-parameters/helpers'
import { useDistributionsUserList } from '../api-queries'
import { useRouteGuestToOwnershipSchedulingLink } from '../api-queries/ownership'
import { useParsedGuest } from './use-parsed-guest'

type OwnershipMatch = {
  expectedHost: RoundRobinExpectedHostV2 // todo not sure it's wise to have it here
  ownershipLinkRoutingResult: OwnershipLinkRoutingResult
  primaryGuestEmail?: string
}

export const useOwnershipRouting = ({
  tenantId,
  ownershipSchedulingLink,
  smartParameterId,
  productFeature,
  searchParams,
  bookerSettings,
  selectedAssigneeIds,
}: {
  tenantId?: string
  ownershipSchedulingLink?: Pick<
    PublicOwnershipSchedulingLink,
    'distributionIds' | 'ownershipSettingsV3' | 'linkId'
  >
  smartParameterId?: string | null
  productFeature?: ProductFeatureV2
  searchParams: URLSearchParams
  bookerSettings?: BookerSettings
  selectedAssigneeIds?: string[]
}): OwnershipMatch | undefined => {
  const linkProductFeature = getLinkProductFeature(productFeature)

  const parsedGuest = useParsedGuest({
    smartParameterId,
    linkProductFeature,
  })

  const primaryGuestEmail = parsedGuest?.guestEmail
  const assignments =
    linkProductFeature?.type === 'OwnershipSchedulingLink'
      ? linkProductFeature.assignments
      : undefined

  const { data: ownershipRouting, isLoading } = useRouteGuestToOwnershipSchedulingLink({
    tenantId,
    ownershipSchedulingLinkId: ownershipSchedulingLink?.linkId,
    data:
      parsedGuest &&
      (!assignments ||
        (assignments.type === 'WithLegacySimpleRule' &&
          ownershipSchedulingLink?.ownershipSettingsV3?.type === 'WithMixedRouting'))
        ? {
            ...parsedGuest,
            crmExplicits: createCrmExplicits(
              searchParams,
              getCrmExplicitsFromMeetingAttemptProductFeature(linkProductFeature)
            ),
          }
        : undefined,
  })

  const distributionIds = useMemo(() => {
    // If we have assignments from meeting attempt use it
    if (assignments) {
      return getDistributionIdsFromOwnershipAssignments(
        assignments,
        ownershipSchedulingLink?.distributionIds
      )
    }

    if (isLoading) {
      return []
    }

    if (ownershipRouting?.assigneesEvaluationV3) {
      return getDistributionIdsFromAssigneeEvaluation(ownershipRouting?.assigneesEvaluationV3)
    } else {
      return ownershipSchedulingLink?.distributionIds
    }
  }, [
    assignments,
    ownershipRouting?.assigneesEvaluationV3,
    ownershipSchedulingLink?.distributionIds,
    isLoading,
  ])

  const distributions = useDistributionsUserList({
    tenantId: tenantId ?? '',
    distributionIds,
    productFeature: 'OwnershipSchedulingLink',
  })

  const isLoadingDistributions = useMemo(() => {
    return distributionIds && distributionIds.length > 0 && distributions.length === 0
  }, [distributionIds, distributions])

  const ownershipLinkRoutingResult = assignments
    ? transformOwnershipAssignmentsToRoutingResult(
        assignments,
        distributions,
        ownershipSchedulingLink,
        ownershipRouting?.assigneesEvaluationV3
      )
    : transformAssigneeEvaluationToRoutingResult(
        ownershipRouting?.assigneesEvaluationV3,
        distributions
      )

  // Apply selectedAssigneeId to the routing result if provided
  const updatedRoutingResult = applySelectedAssigneesToRoutingResult(
    ownershipLinkRoutingResult,
    selectedAssigneeIds
  )

  if (!updatedRoutingResult || isLoadingDistributions) {
    return undefined
  }

  const firstAssignment = getFirstAssignmentFromRoutingResult(updatedRoutingResult)

  const expectedHost = getRoundRobinAndOwnershipExpectedHost({
    bookerSettings,
    firstAssignment: firstAssignment,
  })

  return {
    primaryGuestEmail,
    expectedHost,
    ownershipLinkRoutingResult: updatedRoutingResult,
  }
}
